import { Component } from '@theme/component';

/**
 * Picks the cart progress bar that matches the visitor's Brazilian state.
 *
 * `snippets/cart-progress.liquid` renders one bar per free shipping amount, each with the
 * states it covers in `data-states`. Visitors from other states, or whose state is unknown,
 * see no bar. The state comes from an IP lookup cached in localStorage. A `?uf=SP` URL parameter overrides
 * it, which helps to test other states.
 */

const STORAGE_KEY = 'theme:cart-progress-state';
const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const LOOKUP_URL = 'https://get.geojs.io/v1/ip/geo.json';
const LOOKUP_TIMEOUT = 5000;
const OVERRIDE_PARAM = 'uf';

/** Normalized state names (as returned by the IP lookup) mapped to their codes. */
const STATE_CODES = /** @type {Record<string, string>} */ ({
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'federal district': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
});

const STATES = new Set(Object.values(STATE_CODES));

/**
 * Visitor state code, `''` when it is unknown or outside Brazil, `null` while not resolved yet.
 * @type {string | null}
 */
let visitorState = readOverride() ?? readCache();

/** @type {Promise<void> | undefined} */
let lookup;

/**
 * @param {string} value
 * @returns {string}
 */
function normalize(value) {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/** @returns {string | null} */
function readOverride() {
  const state = new URL(window.location.href).searchParams.get(OVERRIDE_PARAM)?.toUpperCase();
  if (!state || !STATES.has(state)) return null;

  writeCache(state);
  return state;
}

/** @returns {string | null} */
function readCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (typeof cached?.state === 'string' && Date.now() - cached.time < MAX_AGE) return cached.state;
  } catch {
    // Storage unavailable or corrupted: look the state up again
  }

  return null;
}

/** @param {string} state */
function writeCache(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, time: Date.now() }));
  } catch {
    // Storage unavailable: the lookup runs again on the next page
  }
}

/** @returns {Promise<string>} */
async function fetchState() {
  const response = await fetch(LOOKUP_URL, {
    credentials: 'omit',
    signal: AbortSignal.timeout(LOOKUP_TIMEOUT),
  });
  if (!response.ok) throw new Error(`IP lookup failed with status ${response.status}`);

  const data = await response.json();
  if (data?.country_code !== 'BR' || typeof data.region !== 'string') return '';

  return STATE_CODES[normalize(data.region)] ?? '';
}

/**
 * Resolves the visitor state once per page, shared by every instance.
 * @returns {Promise<void>}
 */
function resolveVisitorState() {
  lookup ??= (async () => {
    if (visitorState !== null) return;

    try {
      visitorState = await fetchState();
      writeCache(visitorState);
    } catch (error) {
      // Hide the bars for this page and retry on the next one
      visitorState = '';
      console.warn('Could not detect the visitor state for free shipping.', error);
    }
  })();

  return lookup;
}

/** @extends {Component} */
class CartProgressRegional extends Component {
  /**
   * The section morph copies the server `hidden` attributes back onto the live bars, and its
   * `updatedCallback` hook only reaches the parsed node, so the choice is reapplied on mutation.
   * @type {MutationObserver | undefined}
   */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#showMatchingBar();

    this.#observer = new MutationObserver(() => this.#showMatchingBar());
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });

    if (visitorState === null) {
      resolveVisitorState().then(() => {
        if (this.isConnected) this.#showMatchingBar();
      });
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#showMatchingBar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** Queries the bars directly because refs can lag behind a morph. */
  #showMatchingBar() {
    const bars = Array.from(this.querySelectorAll('.cart-progress[data-states]'));
    const state = visitorState ?? '';
    const match = state ? bars.find((bar) => bar instanceof HTMLElement && bar.dataset.states?.split(',').includes(state)) : undefined;

    for (const bar of bars) {
      if (!(bar instanceof HTMLElement)) continue;

      // Only write on change so the observer does not loop on its own mutations
      const hidden = bar !== match;
      if (bar.hidden !== hidden) bar.hidden = hidden;
    }
  }
}

if (!customElements.get('cart-progress-regional')) {
  customElements.define('cart-progress-regional', CartProgressRegional);
}
